/**
 * @file OrdersAttentionPage.tsx
 * @description «Требуют внимания» — где доставка встала (phase19.13 шаг 5;
 * phase19.15 — заявки на согласование; phase19.31 — таблица по разбору A).
 *
 * Рабочая очередь менеджера одной таблицей: заказ · проблема («N дн») · с
 * какого события · перевозчик словом · трек. Отбор — пилюли со счётчиками
 * (SC45), выбранная живёт в адресе (`?rule=`); на телефоне пилюли — в шторке
 * «Фильтры · N». «Обновить» — кнопка-контур в верхней панели, на телефоне —
 * строкой под шапкой (SC67: без «⋯»).
 *
 * Перечитывание тихое: прежняя таблица остаётся на экране, пока идёт запрос.
 */

import { CheckCircle2, RefreshCw, Search } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router";

import { ATTENTION_COLUMNS } from "../../../components/orders/attention/attention-columns";
import { Button } from "../../../components/ui/button";
import { CabinetPageHeader } from "../../../components/ui/CabinetPageHeader";
import { FilterBar } from "../../../components/ui/FilterBar";
import { FilterChips, type FilterChip } from "../../../components/ui/FilterChips";
import { ResponsiveList } from "../../../components/ui/ResponsiveList";
import { ApiError } from "../../../lib/api-client";
import { listShipmentsAttention, type ShipmentAttentionListDto } from "../../../lib/api-delivery-monitoring";
import {
  attentionChips,
  buildAttentionRows,
  filterAttentionRows,
  parseAttentionFilter,
  type AttentionFilter,
} from "../../../lib/order-attention-rows";

export function OrdersAttentionPage() {
  const { slug } = useParams<{ slug: string }>();
  const ordersBasePath = `/shop/${slug ?? ""}/orders`;
  const [params, setParams] = useSearchParams();
  const filter = parseAttentionFilter(params.get("rule"));

  const [data, setData] = useState<ShipmentAttentionListDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      setData(await listShipmentsAttention());
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить список. Проверьте связь и попробуйте ещё раз");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  function setFilter(next: AttentionFilter) {
    const search = new URLSearchParams(params);
    if (next === "all") search.delete("rule");
    else search.set("rule", next);
    setParams(search, { replace: true });
  }

  const rows = data ? filterAttentionRows(buildAttentionRows(data), filter) : [];
  const chips: FilterChip<AttentionFilter>[] = data
    ? attentionChips(data).map((chip) => ({
        value: chip.value,
        label: chip.label,
        count: chip.count,
        tone: chip.danger ? "danger" : "default",
      }))
    : [];

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader
        title="Требуют внимания"
        subtitle="Где встала доставка и какие заявки ждут расчёта · сверху самые давние"
        back={{ to: ordersBasePath, label: "К реестру заказов" }}
        // Видимой кнопкой и на телефоне — строкой под шапкой, без «⋯» (SC67).
        secondaryActions={
          <Button variant="outline" onClick={() => void refresh()} loading={loading}>
            <RefreshCw className="size-4" aria-hidden />
            Обновить
          </Button>
        }
      />

      {/* Телефон: пять пилюль занимали четыре строки — уходят в шторку
          «Фильтры · N» (тот же приём, что в реестрах склада и заказов). */}
      {chips.length > 0 ? (
        <FilterBar
          chipsCollapse={{ activeCount: filter === "all" ? 0 : 1, onReset: () => setFilter("all"), title: "Отбор по проблеме" }}
          chips={<FilterChips items={chips} value={filter} onChange={setFilter} aria-label="Отбор по проблеме" />}
        />
      ) : null}

      <ResponsiveList
        aria-label="Заказы, которые требуют внимания"
        rows={rows}
        columns={ATTENTION_COLUMNS}
        rowKey={(row) => row.key}
        rowHref={(row) => `${ordersBasePath}/${row.orderId}`}
        rowLabel={(row) => `Заказ ${row.orderNumber}: ${row.problem}`}
        rowSize="double"
        loading={loading}
        error={error}
        onRetry={() => void refresh()}
        filtered={filter !== "all"}
        empty={{
          icon: CheckCircle2,
          title: "Все отправления в порядке",
          description:
            "Зависшие отправления и просроченные заявки появятся здесь сами — пороги настраиваются в разделе «Доставка».",
        }}
        filteredEmpty={{ icon: Search, title: "По этой проблеме сейчас ничего нет", onReset: () => setFilter("all"), resetLabel: "Показать все" }}
      />
    </div>
  );
}
